/**
 * Thumbnail of a document that is already uploaded, shown inside the edit form. The files live in a
 * private bucket, so the image comes from a short-lived signed link requested on demand.
 */

import { useQuery } from '@tanstack/react-query';
import { ExternalLink, FileText, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DocumentKind } from '../types';

interface ExistingDocumentPreviewProps {
  kind: DocumentKind;
  /** Identifies whose document it is (token or participant id), so previews are never mixed up */
  cacheKey: string;
  load: (kind: DocumentKind) => Promise<{ url: string; isPdf: boolean } | null>;
  className?: string;
}

export function ExistingDocumentPreview({
  kind,
  cacheKey,
  load,
  className,
}: ExistingDocumentPreviewProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['existing_document', cacheKey, kind],
    queryFn: () => load(kind),
    // Signed links last 5 minutes
    staleTime: 4 * 60 * 1000,
    retry: false,
  });

  if (isLoading) {
    return (
      <p
        className={cn(
          'flex items-center gap-2 text-xs text-muted-foreground',
          className,
        )}
      >
        <Loader2 className="size-3.5 animate-spin" /> Cargando documento actual…
      </p>
    );
  }
  if (error) {
    return (
      <p className={cn('text-xs text-destructive', className)}>
        No se pudo mostrar el documento actual.
      </p>
    );
  }
  if (!data) return null;

  return (
    <div className={cn('space-y-1', className)}>
      <p className="text-xs font-medium text-muted-foreground">
        Documento actual
      </p>
      {data.isPdf ? (
        <a
          href={data.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted"
        >
          <FileText className="size-4" /> Abrir PDF{' '}
          <ExternalLink className="size-3.5" />
        </a>
      ) : (
        <a
          href={data.url}
          target="_blank"
          rel="noreferrer"
          title="Abrir en tamaño completo"
        >
          <img
            src={data.url}
            alt="Documento actual"
            className={cn(
              'max-h-48 rounded-md border object-contain',
              // The signature is a transparent PNG drawn in black: give it a white page
              kind === 'signature' && 'bg-white p-2',
            )}
          />
        </a>
      )}
    </div>
  );
}
